/* eslint-disable no-case-declarations */
import { LayoutSplashScreen } from '_metronic/layout/core';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { VJFPCache } from 'app/services/CacheUtils';
import { QueueUserService } from 'app/services/QueueUserService';
import { VJFPUserService } from 'app/services/VJFPUserService';
import { Auth, Hub } from 'aws-amplify';
import { HubCallback } from 'global';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { clearAllAmplifySubscriptions, setupInterviewSessionSubscriptionsByCandidateId, setupQueueSubscriptionsByCandidateId } from '../amplify-subscriptions';
import { UserStatus, VJFPUser } from '../API';
import { CognitoUserResponse, IUser, Roles } from '../types';
import { config } from './../../config';

const getCookieDomain = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') return 'localhost';
  if (hostname.includes(config.amplifyDomain)) return `.${config.amplifyDomain}`;
  return hostname; // Returns exactly the current amplify test URL to permit cookies
};

Auth.configure({
  cookieStorage: {
    domain: getCookieDomain(),
    secure: window.location.hostname !== 'localhost',
    expires: 1
  }
});

/* IAuthContext interface is used as return type of useAuth() consumer */

interface IAuthContext {
  user: IUser | null | undefined;
  login(username: string, password: string): Promise<CognitoUserResponse>;
  logout(organizationId: string | undefined, userId: number, jobFairId?: string): Promise<any>;
  completeNewPassword(user: any, password: string): Promise<any>;
}

// Type safety for react Props
interface Props {
  children?: ReactNode
}

// delegate login to Amplify's signin method
const login = (username: string, password: string): Promise<CognitoUserResponse> => Auth.signIn(username, password);

// delegate completeNewPassword to Amplify's completeNewPassword method ; used in forgot password functionality as it 2 step process after code verification
const completeNewPassword = (user: any, password: string) => Auth.completeNewPassword(user, password);

// delegate logout to Amplify's signOut method ; Also it updates the status as logged out from presence table.

const logout = async (organizationId: string | undefined, userId: number, jobFairId?: string): Promise<any> => {
  try {
    if (organizationId) {
      await new VJFPUserService().updateStatus(organizationId, userId, UserStatus.LOGGED_OUT, jobFairId);
    }
  } finally {
    await Auth.signOut();
  }
};

// Get session from local storage
const getSession = (): Promise<CognitoUserSession | null> => Auth.currentSession();

// seggregated functionality from provider
const useCognito = () => {
  // user State
  const [user, setUser] = useState<IUser | null | undefined>(undefined);
  // Get Presence Status Helper , If there is no presence, we make sure to return logged-in status from here

  const getPresenceStatusFromVJFPUser = (vjfpUser: VJFPUser): UserStatus => {
    switch (vjfpUser?.presenceStatus) {
      case UserStatus.AWAY:
      case UserStatus.LOGGED_IN:
        return vjfpUser?.presenceStatus;
      case UserStatus.DISCONNECTED:
      case UserStatus.LOGGED_OUT:
      default:
        return UserStatus.LOGGED_IN;
    }
  };

  const getPresenceStatus = async (organizationId: string, userId: number): Promise<UserStatus> => {
    const vjfpUser = await new VJFPUserService().getById(organizationId, userId);
    return vjfpUser ? getPresenceStatusFromVJFPUser(vjfpUser) : UserStatus.LOGGED_OUT;
  };

  // Amplify relies on HubCallback events, so we are leveraging same here

  const authListener: HubCallback = async ({ payload: { event, data } }) => {
    /* There could multiple events like 'signIn', 'signOut', 'signIn_failiure' , etc...
    We will using these events to proceed to set userState for othe components */

    switch (event) {
      case 'signIn':
        const user = await new VJFPUserService().getByEmail(data.attributes.email);
        if (!user) { return; }

        const _roles = data.attributes['custom:roles']?.split(',') || [];

        const userData: IUser = {
          id: user.id,
          organizationId: user.organizationId,
          username: data.username,
          firstName: data.attributes.given_name,
          lastName: data.attributes.family_name,
          email: data.attributes.email,
          roles: _roles,
          token: data.signInUserSession.accessToken,
          status: await getPresenceStatus(user.organizationId, user.id),
          isAvailableForChat: !_roles.includes(Roles.Recruiter),
          sponsorId: data.attributes['custom:sponsorId'],
          avatar: user.avatar || undefined
        };

        setUser(userData); // sets user
        VJFPCache.clear();
        if (_roles.includes(Roles.Applicant)) {
          setupQueueSubscriptionsByCandidateId(userData.id);
          setupInterviewSessionSubscriptionsByCandidateId(userData.id);
        }
        break;

      case 'signOut':
        // Clear all storage
        localStorage.clear();
        sessionStorage.clear();

        // Clear cookies
        // eslint-disable-next-line no-case-declarations
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }

        // set user state is null , as user is no longer available
        setUser(null);

        // unsubscribe from all active subscriptions
        clearAllAmplifySubscriptions();
        break;
    }
  };

  // Hub Events subscription setup
  useEffect(() => {
    Hub.listen('auth', authListener);
    return () => Hub.remove('auth', authListener);
  }, []);

  // In case user is already logged in , we can get currentUserInfo() from local storage session and update user state. No need to re-login
  useEffect(() => {
    getSession().then((session) => {
      VJFPCache.clear();
      if (session && session.isValid()) {
        console.log('session valid');

        Auth.currentUserInfo().then(async (user: any) => {
          if (user.attributes) {
            const userAttributes = user.attributes;

            const dbUser = await new VJFPUserService().getByEmail(userAttributes.email);
            if (!dbUser) { return; }

            window.sessionStorage.setItem('VJFPUserId', dbUser.id.toString());
            const vjfpUserService = new VJFPUserService();

            if (!dbUser?.id) {
              alert('User not found');
              return;
            }

            const newStatus = getPresenceStatusFromVJFPUser(dbUser);

            try {
              setUser({
                id: dbUser.id,
                organizationId: dbUser.organizationId,
                username: userAttributes.sub,
                firstName: userAttributes.given_name,
                lastName: userAttributes.family_name,
                email: userAttributes.email,
                roles: dbUser.roles,
                token: session.getAccessToken(),
                status: newStatus,
                isAvailableForChat: !dbUser.roles.includes(Roles.Recruiter),
                sponsorId: userAttributes['custom:sponsorId'],
                avatar: dbUser.avatar || undefined
              });
              if (dbUser.roles.includes(Roles.Applicant)) {
                setupQueueSubscriptionsByCandidateId(dbUser.id);
                setupInterviewSessionSubscriptionsByCandidateId(dbUser.id);
              }
            } catch (err) {
              console.log(err);
            }

            if (dbUser?.organizationId && dbUser.id && dbUser.presenceStatus !== newStatus) {
              await vjfpUserService.updateStatus(dbUser.organizationId, dbUser.id, newStatus);

              // If Role is Recruiter we need to Process Queue for the very first time (Note this will call process request and not immediately process queue)
              if (dbUser.roles.includes(Roles.Recruiter) && dbUser.lastPresenceJobFairId) {
                try {
                  new QueueUserService().processQueue(dbUser.lastPresenceJobFairId);
                } catch (err) {
                  console.log(err);
                }
              }
            }
          } else {
            setUser(null);
          }
        });
      } else {
        console.log('session not valid or do not exists');
        setUser(null);
      }
    }).catch(err => {
      console.log('Session Error', err);
      setUser(null);
    });
    return () => clearAllAmplifySubscriptions();
  }, []);

  // return IAuthContext
  return { user, login, logout, completeNewPassword };
};

// Context based on IAuthContext
const AuthContext = createContext<IAuthContext>({
  user: undefined,
  login,
  logout,
  completeNewPassword
});

// Context Consumer
export const useAuth = () => {
  return useContext(AuthContext);
};

// Context Provider
export const AuthProvider = ({ children }: Props) => {
  const auth = useCognito();
  return (<AuthContext.Provider value={auth}>
    {auth.user === undefined ? <LayoutSplashScreen /> : children}
  </AuthContext.Provider>);
};
